<template>
  <div class="forgot-password-container">
    <!-- Step 1: Request OTP -->
    <div v-if="currentStep === 1" class="step-container">
      <div class="text-center mb-6">
        <h2 class="text-2xl font-bold text-gray-900 mb-2">
          {{ $t("forgot_password.title") }}
        </h2>
        <p class="text-gray-600">
          {{ $t("forgot_password.subtitle") }}
        </p>
      </div>

      <form @submit.prevent="requestOTP" class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t("forgot_password.email_label") }}
          </label>
          <input
            v-model="formData.email"
            type="email"
            :placeholder="$t('forgot_password.email_placeholder')"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            :class="{ 'border-red-500': errors.email }"
            required
          />
          <p v-if="errors.email" class="text-red-500 text-sm mt-1">
            {{ errors.email }}
          </p>
        </div>

        <Button
          type="submit"
          :loading="isLoading"
          :disabled="!formData.email || isLoading"
          class="w-full bg-primary-900 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
        >
          {{ $t("forgot_password.request_otp") }}
        </Button>
      </form>

      <div class="text-center mt-4">
        <button
          @click="$emit('back-to-login')"
          class="text-primary-900 hover:text-primary-700 text-sm"
        >
          {{ $t("forgot_password.back_to_login") }}
        </button>
      </div>
    </div>

    <!-- Step 2: Verify OTP -->
    <div v-if="currentStep === 2" class="step-container">
      <div class="text-center mb-6">
        <h2 class="text-2xl font-bold text-gray-900 mb-2">
          {{ $t("forgot_password.verify_otp_title") }}
        </h2>
        <p class="text-gray-600">
          {{
            $t("forgot_password.verify_otp_subtitle", { email: formData.email })
          }}
        </p>
      </div>

      <form @submit.prevent="verifyOTP" class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t("forgot_password.otp_label") }}
          </label>
          <input
            v-model="formData.otp"
            type="text"
            :placeholder="$t('forgot_password.otp_placeholder')"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-center text-lg tracking-widest"
            :class="{ 'border-red-500': errors.otp }"
            maxlength="6"
            required
          />
          <p v-if="errors.otp" class="text-red-500 text-sm mt-1">
            {{ errors.otp }}
          </p>
        </div>

        <div class="flex space-x-3">
          <Button
            type="button"
            @click="resendOTP"
            :loading="isResending"
            :disabled="isResending"
            variant="outlined"
            class="flex-1 border-primary-900 text-primary-900 hover:bg-primary-50"
          >
            {{ $t("forgot_password.resend_otp") }}
          </Button>
          <Button
            type="submit"
            :loading="isLoading"
            :disabled="!formData.otp || isLoading"
            class="flex-1 bg-primary-900 text-white hover:bg-primary-700"
          >
            {{ $t("forgot_password.verify_otp") }}
          </Button>
        </div>
      </form>

      <div class="text-center mt-4">
        <button
          @click="currentStep = 1"
          class="text-primary-900 hover:text-primary-700 text-sm"
        >
          {{ $t("forgot_password.change_email") }}
        </button>
      </div>
    </div>

    <!-- Step 3: Reset Password -->
    <div v-if="currentStep === 3" class="step-container">
      <div class="text-center mb-6">
        <h2 class="text-2xl font-bold text-gray-900 mb-2">
          {{ $t("forgot_password.reset_password_title") }}
        </h2>
        <p class="text-gray-600">
          {{ $t("forgot_password.reset_password_subtitle") }}
        </p>
      </div>

      <form @submit.prevent="resetPassword" class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t("forgot_password.new_password_label") }}
          </label>
          <div class="relative">
            <input
              v-model="formData.newPassword"
              :type="showNewPassword ? 'text' : 'password'"
              :placeholder="$t('forgot_password.new_password_placeholder')"
              class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              :class="{ 'border-red-500': errors.newPassword }"
              required
            />
            <button
              type="button"
              @click="showNewPassword = !showNewPassword"
              class="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <v-icon class="text-gray-400">{{
                showNewPassword ? "mdi-eye-off" : "mdi-eye"
              }}</v-icon>
            </button>
          </div>
          <p v-if="errors.newPassword" class="text-red-500 text-sm mt-1">
            {{ errors.newPassword }}
          </p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t("forgot_password.confirm_password_label") }}
          </label>
          <div class="relative">
            <input
              v-model="formData.confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              :placeholder="$t('forgot_password.confirm_password_placeholder')"
              class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              :class="{ 'border-red-500': errors.confirmPassword }"
              required
            />
            <button
              type="button"
              @click="showConfirmPassword = !showConfirmPassword"
              class="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <v-icon class="text-gray-400">{{
                showConfirmPassword ? "mdi-eye-off" : "mdi-eye"
              }}</v-icon>
            </button>
          </div>
          <p v-if="errors.confirmPassword" class="text-red-500 text-sm mt-1">
            {{ errors.confirmPassword }}
          </p>
        </div>

        <Button
          type="submit"
          :loading="isLoading"
          :disabled="!isFormValid || isLoading"
          class="w-full bg-primary-900 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
        >
          {{ $t("forgot_password.reset_password") }}
        </Button>
      </form>
    </div>

    <!-- Success Message -->
    <div v-if="currentStep === 4" class="step-container text-center">
      <div class="mb-6">
        <v-icon class="text-6xl text-green-500 mb-4">mdi-check-circle</v-icon>
        <h2 class="text-2xl font-bold text-gray-900 mb-2">
          {{ $t("forgot_password.success_title") }}
        </h2>
        <p class="text-gray-600">
          {{ $t("forgot_password.success_message") }}
        </p>
      </div>

      <Button
        @click="$emit('back-to-login')"
        class="bg-primary-900 text-white py-2 px-6 rounded-md hover:bg-primary-700 transition-colors"
      >
        {{ $t("forgot_password.back_to_login") }}
      </Button>
    </div>
  </div>
</template>

<script>
import { ref, computed, reactive } from "vue";
import { Button } from "primevue";
import { useStore } from "vuex";
import { getTenant } from "../services/tenant.js";

export default {
  name: "ForgotPassword",
  components: {
    Button,
  },
  emits: ["back-to-login"],
  setup() {
    const store = useStore();
    const currentStep = ref(1);
    const isLoading = ref(false);
    const isResending = ref(false);
    const showNewPassword = ref(false);
    const showConfirmPassword = ref(false);
    const username = ref("");

    const formData = reactive({
      email: "",
      otp: "",
      newPassword: "",
      confirmPassword: "",
    });

    const errors = reactive({
      email: "",
      otp: "",
      newPassword: "",
      confirmPassword: "",
    });

    // Computed properties
    const isFormValid = computed(() => {
      return (
        formData.newPassword &&
        formData.confirmPassword &&
        formData.newPassword === formData.confirmPassword &&
        formData.newPassword.length >= 10 &&
        /^(?=.*[A-Za-z])(?=.*\d)/.test(formData.newPassword)
      );
    });

    // Methods
    const clearErrors = () => {
      Object.keys(errors).forEach((key) => {
        errors[key] = "";
      });
    };

    const validateEmail = () => {
      if (!formData.email) {
        errors.email = "Email is required";
        return false;
      }
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = "Please enter a valid email address";
        return false;
      }
      return true;
    };

    const validateOTP = () => {
      if (!formData.otp) {
        errors.otp = "OTP is required";
        return false;
      }
      if (formData.otp.length !== 6) {
        errors.otp = "OTP must be 6 digits";
        return false;
      }
      if (!/^\d+$/.test(formData.otp)) {
        errors.otp = "OTP must contain only numbers";
        return false;
      }
      return true;
    };

    const validatePassword = () => {
      if (!formData.newPassword) {
        errors.newPassword = "Password is required";
        return false;
      }
      if (formData.newPassword.length < 10) {
        errors.newPassword = "Password must be at least 10 characters";
        return false;
      }
      if (!/^(?=.*[A-Za-z])(?=.*\d)/.test(formData.newPassword)) {
        errors.newPassword = "Password must contain both letters and numbers";
        return false;
      }
      if (formData.newPassword !== formData.confirmPassword) {
        errors.confirmPassword = "Passwords do not match";
        return false;
      }
      return true;
    };

    const requestOTP = async () => {
      clearErrors();
      if (!validateEmail()) return;

      isLoading.value = true;
      try {
        const response = await store.dispatch("requestForgotPasswordOTP", {
          email: formData.email,
          domain: getDomain(),
        });

        if (response.success) {
          currentStep.value = 2;
          username.value = response.username;
        } else {
          errors.email =
            response.message || "Failed to send OTP. Please try again.";
        }
      } catch (error) {
        errors.email =
          "Network error. Please check your connection and try again.";
      } finally {
        isLoading.value = false;
      }
    };

    const verifyOTP = async () => {
      clearErrors();
      if (!validateOTP()) return;

      isLoading.value = true;
      try {
        const response = await store.dispatch("verifyForgotPasswordOTP", {
          otp: formData.otp,
          domain: getDomain(),
          uname: username.value,
        });

        if (response.success) {
          currentStep.value = 3;
        } else {
          errors.otp = response.message || "Invalid OTP. Please try again.";
        }
      } catch (error) {
        errors.otp =
          "Network error. Please check your connection and try again.";
      } finally {
        isLoading.value = false;
      }
    };

    const resendOTP = async () => {
      isResending.value = true;
      try {
        await requestOTP();
      } finally {
        isResending.value = false;
      }
    };

    const resetPassword = async () => {
      clearErrors();
      if (!validatePassword()) return;

      isLoading.value = true;
      try {
        const response = await store.dispatch("resetForgotPassword", {
          otp: formData.otp,
          newPassword: formData.newPassword,
          domain: getDomain(),
          uname: username.value,
        });

        if (response.success) {
          currentStep.value = 4;
        } else {
          errors.newPassword =
            response.message || "Failed to reset password. Please try again.";
        }
      } catch (error) {
        errors.newPassword =
          "Network error. Please check your connection and try again.";
      } finally {
        isLoading.value = false;
      }
    };

    const getDomain = () => getTenant();

    return {
      currentStep,
      isLoading,
      isResending,
      username,
      showNewPassword,
      showConfirmPassword,
      formData,
      errors,
      isFormValid,
      validateEmail,
      requestOTP,
      verifyOTP,
      resendOTP,
      resetPassword,
      getDomain,
    };
  },
};
</script>

<style scoped>
.forgot-password-container {
  max-width: 400px;
  margin: 0 auto;
  padding: 2rem;
}

.step-container {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

input:focus {
  box-shadow: 0 0 0 3px rgba(6, 117, 74, 0.1);
}
</style>
